import React, { useContext, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperComponent from 'swiper'
import { GRIS, WHITE } from '../../theme'
import SvgCircledLeftArrow from '../../icons/SvgCircledLeftArrow'
import SvgCircledRightArrow from '../../icons/SvgCircledRightArrow'
import { AutresElementsCrossSellingModel } from './types/autresElementsCrossSellingModel'
import AutreElementCrossSellingCard from './AutreElementCrossSellingCard'
import { INITIAL_FORMULAIRE_RECHERCHE } from '../../modules/resultats-recherche/types/formulaireRechercheModel'
import { objectGetKeys } from '../../utils/ieCompatibility'
import { isInViewport } from '../../utils/utils'
import { makeStyles } from 'tss-react/mui'
import { Grid, useTheme } from '@mui/material'
import {
	biensTypeAppartement,
	biensTypeParking,
	champsMenuRechercheEnum,
	getKeyFromLocalisationRechercheValue,
	localisationRechercheEnum
} from '../../modules/resultats-recherche/enums/champsMenuRecherche'
import { directionEnum } from './enums/directionEnum'
import { lotTypesEnum } from '../../enums/lot/lotTypesEnum'
import { bordEnum } from './enums/bordEnum'
import { ResponsiveContext } from '../../utils/context/ResponsiveContext'
import linksEnum from '../../enums/linksEnum'
import Link from '../link/Link'
import { FormattedMessage } from 'react-intl'
import { useLocation, useNavigate } from 'react-router'
import { RechercheContext } from '../../modules/resultats-recherche/utils/RechercheContext'

const useStyles = makeStyles<{ isLargeScreen: boolean, width: number, height: number | string, path: string }>()(
	(theme, { isLargeScreen, width, height, path }) => ({
		swiperContainer: {
			overflow: 'hidden',
			flexWrap: 'nowrap',
			position: 'relative',
			background: GRIS,
			paddingBottom: path === linksEnum.RECHERCHE_OFFRE ? 117 : 163,
			width: '100%',
			boxSizing: 'border-box',

			'& > div:first-child': {
				padding: 1
			}
		},
		swiper: {
			width: '100%',
			height: height,
			position: 'unset',
			overflow: 'visible',
			margin: 0,

			'& .swiper-wrapper': {
				display: 'inline-flex'
			},
			'& .swiper-slide': {
				maxWidth: width
			}
		},
		marginFirstSwiper: {
			marginLeft: isLargeScreen ? 65 : 30
		},
		swiperButtonsContainer: {
			marginTop: 35,
			paddingLeft: isLargeScreen ? 65 : 30,
			paddingRight: isLargeScreen ? 65 : 30
		},
		swiperButton: {
			marginLeft: 11,
			cursor: 'pointer'
		},
		decouvrirAppartement: {
			borderRadius: 25,
			marginTop: isLargeScreen ? 'unset' : 30
		},
		boutonFlechesContainer: {
			display: 'flex'
		}
	})
)

interface AcceptingProps {
	elementsCrossSelling: AutresElementsCrossSellingModel[],
	width: number,
	height: number | string,
	spaceBetweenSlides: number,
	defilementAutomatique?: boolean,
	type?: keyof typeof lotTypesEnum,
	codePostal?: string,
	swiperContainerStyle?: string,
	showFavorite?: boolean,
	// Props permettant de mettre à jour le swiper, utile dans le cas ou la taille du swiper change dans la view
	// pour réactualiser la taille du swiper (ex: passage de md={12} à md={6})
	doitMaj?: boolean
}

type AutresElementsCrossSellingProps = AcceptingProps

const AutresElementsCrossSelling: React.FC<AutresElementsCrossSellingProps> = (
	{
		elementsCrossSelling,
		width,
		height,
		spaceBetweenSlides,
		defilementAutomatique = false,
		type = lotTypesEnum.apartment,
		codePostal = '',
		swiperContainerStyle,
		showFavorite = false,
		doitMaj
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const theme = useTheme()

	const location = useLocation()
	const navigate = useNavigate()

	const { classes, cx } = useStyles({ isLargeScreen, width, height, path: location.pathname })
	const { setOffreSurvolee } = useContext(RechercheContext)

	const [flechesVisibles, setFlechesVisibles] = useState(true)
	const [swiper, setSwiper] = useState<SwiperComponent>()
	const [currentEdge, setCurrentEdge] = useState<bordEnum>(bordEnum.debut)

	// Boolean permettant de définir pour quelles pages on affiche les flèches du swiper
	const pageAvecBoutonSwiper = ((location.pathname.includes(linksEnum.OFFRE) || location.pathname === linksEnum.RECHERCHE_OFFRE) && isLargeScreen)

	// Boolean permettant de définir pour quelles pages on affiche le bouton pour voir plus d'appartements
	const pageAvecBoutonVoirPlus = location.pathname.includes(linksEnum.OFFRE) || location.pathname === linksEnum.RECHERCHE_OFFRE

	const swiperParentRef = useRef<HTMLDivElement | null>(null)

	const onButtonClicked = (direction: directionEnum) => {
		if (direction === directionEnum.next) {
			swiper && swiper.slideNext()
		} else {
			swiper && swiper.slidePrev()
		}
	}

	// On met à jour le swiper lors du changement de la props, pour réactualiser la taille du swiper si le conteneur à changer
	useEffect(
		() => {
			if (swiper) {
				swiper.update()
			}
		}, [swiper, doitMaj]
	)

	// On vérifie si le code postal fait parti de la liste connue des codes postaux de l'appli, si c'est le cas on récupère sa clé, sinon on filtre sur tous les codes postaux
	const verificationExistenceCodePostal = () => getKeyFromLocalisationRechercheValue(codePostal) ? [getKeyFromLocalisationRechercheValue(codePostal) as keyof typeof localisationRechercheEnum] : objectGetKeys(localisationRechercheEnum)

	// Mise en place du filtre sur le type de bien selon le type de l'offre
	const recuperationTypeDeBien = () => {
		// Si on est sur la page d'une offre
		if (location.pathname.includes(linksEnum.OFFRE)) {
			// Si c'est un appartement, on récupère tous les types de bien sauf "Parking voiture" & "Parking moto"
			if (type === lotTypesEnum.apartment) {
				return biensTypeAppartement
			} else {
				return biensTypeParking
			}
		} else {
			return INITIAL_FORMULAIRE_RECHERCHE[champsMenuRechercheEnum.typeBien]
		}
	}

	// Vérification de la présence de toutes les vignettes dans le viewport (taille de l'écran)
	// Si toutes les vignettes sont présentes, on masque les flèches directionnelles du swiper
	// Le swiper et les sliders ne pouvant pas avoir de ref, on part du parent et on récupère la dernière slide du swiper
	useEffect(
		() => {
			if (swiperParentRef.current) {
				const refChild = swiperParentRef.current?.children as HTMLCollection
				if (refChild) {
					const swiperElement: Element = refChild[0]
					const swiperWrapper = swiperElement.children as HTMLCollection

					if (swiperWrapper) {
						const derniereSlide = swiperWrapper[swiperWrapper.length - 1].lastChild as Element
						setFlechesVisibles(!isInViewport(derniereSlide))
					}
				}
			}
		}, [swiperParentRef, elementsCrossSelling]
	)

	return (
		<Grid
			container
			item
			xs={12}
			direction="column"
			className={
				cx(
					classes.swiperContainer,
					swiperContainerStyle
				)
			}
		>
			<Grid item ref={swiperParentRef}>
				<Swiper
					onSwiper={(s: SwiperComponent) => setSwiper(s)}
					onToEdge={s => {
						if (s.isBeginning) {
							setCurrentEdge(bordEnum.debut)
						} else {
							setCurrentEdge(bordEnum.fin)
						}
					}}
					onFromEdge={s => {
						if (!s.isBeginning && !s.isEnd) {
							setCurrentEdge(bordEnum.aucun)
						}
					}}
					className={cx(
						classes.swiper,
						{ [classes.marginFirstSwiper]: currentEdge === bordEnum.debut }
					)}
					slidesPerView="auto"
					spaceBetween={spaceBetweenSlides}
					autoplay={defilementAutomatique ? {
						delay: 5000,
						disableOnInteraction: false
					} : false}
				>
					{
						elementsCrossSelling.map(
							(elementCrossSelling, index) => (
								<SwiperSlide
									key={`elementCrossSelling-${index}`}
									onMouseEnter={() => setOffreSurvolee(elementCrossSelling.alias)}
									onMouseLeave={() => setOffreSurvolee(null)}
								>
									<AutreElementCrossSellingCard elementCrossSelling={elementCrossSelling} showFavorite={showFavorite} />
								</SwiperSlide>
							)
						)
					}
				</Swiper>
			</Grid>

			{
				(pageAvecBoutonVoirPlus || (pageAvecBoutonSwiper && flechesVisibles)) && (
					<Grid container item direction="row" xs={12} justifyContent={pageAvecBoutonVoirPlus ? 'space-between' : 'flex-end'} className={classes.swiperButtonsContainer}>
						{
							// Bouton afficher uniquement sur la page des offres & accueil
							pageAvecBoutonVoirPlus && (
								<Grid item>

									<Link
										textStyle="text4"
										href={`${linksEnum.RECHERCHE_OFFRE}?filtres=${JSON.stringify({
											...INITIAL_FORMULAIRE_RECHERCHE,
											[champsMenuRechercheEnum.typeBien]: recuperationTypeDeBien(),
											[champsMenuRechercheEnum.location]: verificationExistenceCodePostal()
										})}`}
										onClick={() => navigate({
											pathname: linksEnum.RECHERCHE_OFFRE,
											search: `?filtres=${JSON.stringify({
												...INITIAL_FORMULAIRE_RECHERCHE,
												[champsMenuRechercheEnum.typeBien]: recuperationTypeDeBien(),
												[champsMenuRechercheEnum.location]: verificationExistenceCodePostal()
											})}`
										})}
										noAnimation
										typeBouton
										className={classes.decouvrirAppartement}
										prevent
									>
										<FormattedMessage
											id={`offre.offresLiees.boutonVoir${location.pathname === linksEnum.RECHERCHE_OFFRE ? 'biens' : type}`}
											defaultMessage="Voir nos biens à louer"
											description="Bouton pour voir les appartements à louer"
										/>
									</Link>

								</Grid>
							)
						}

						{
							// On masque affiche les flèches si on est sur le détail du résidence
							// Ou si on est en desktop sur le détail d'une offre
							// Ou si on est en desktop sur l'accueil
							pageAvecBoutonSwiper && flechesVisibles && (
								<div className={classes.boutonFlechesContainer}>
									<Grid onClick={() => onButtonClicked(directionEnum.previous)} className={classes.swiperButton}>
										{
											currentEdge !== bordEnum.debut ?
												<SvgCircledLeftArrow color={theme.palette.primary.main} secondColor={theme.palette.primary.main} thirdColor={WHITE} />
												: <SvgCircledLeftArrow color={theme.palette.blue[10]} secondColor={theme.palette.blue[20]} thirdColor={theme.palette.blue[20]} />
										}
									</Grid>
									<Grid onClick={() => onButtonClicked(directionEnum.next)} className={classes.swiperButton}>
										{currentEdge !== bordEnum.fin ?
											<SvgCircledRightArrow color={theme.palette.primary.main} /> :
											<SvgCircledRightArrow color={theme.palette.blue[10]} secondColor={theme.palette.blue[20]} thirdColor={theme.palette.blue[20]} />
										}
									</Grid>
								</div>
							)
						}
					</Grid>
				)
			}
		</Grid>
	)
}

export default AutresElementsCrossSelling